import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { LocationPersonaControl, LocationPersonaControlViewState } from 'owa-location-persona';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import { RoomIcon } from 'owa-room-control';
import shouldRenderCity from '../utils/shouldRenderCity';
import CommonFloatingPickerItem from 'owa-readwrite-common-well/lib/components/CommonFloatingPickerItem';
import type { ITheme } from '@fluentui/style-utilities';
import getAvailabilityTextAndStyle from '../utils/getAvailabilityTextAndStyle';
import { isFeatureEnabled } from 'owa-feature-flags';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';

import styles from './LocationSuggestionItem.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

export interface LocationPickerItemProps {
    locationResult: LocationPersonaControlViewState;
    colorScheme: ColorScheme;
    roomFreeBusyEnabled: boolean;
    itemProps: any;
    theme?: ITheme;
}

/**
 * This the location picker suggestion, used to render an entry in the location search drop down
 */
export default observer(function LocationSuggestionItem(props: LocationPickerItemProps) {
    let { locationResult, itemProps, colorScheme, theme } = props;
    let hasAddress = !isNullOrWhiteSpace(locationResult.displayAddress);
    let isRoom = locationResult.personaType == 'Room';
    let isRoomWithAvailabilityInfo = isRoom && props.roomFreeBusyEnabled;
    let hasSecondLine = hasAddress || isRoomWithAvailabilityInfo;
    let iconImage = isRoom ? (
        <RoomIcon colorScheme={colorScheme} />
    ) : (
        <LocationPersonaControl locationPersona={locationResult} size={PersonaSize.size32} />
    );
    return (
        <div className={classNames(styles.container)} title={locationResult.displayName}>
            <CommonFloatingPickerItem
                pickerItemData={locationResult}
                isHighlighted={itemProps.suggestionModel.selected}
                personaControl={getLocationPersonaControl(iconImage)}
                firstLineElement={getFirstLine(locationResult)}
                secondLineElement={
                    hasSecondLine && getSecondLine(locationResult, isRoomWithAvailabilityInfo)
                }
                theme={theme}
            />
        </div>
    );
});

function getLocationPersonaControl(iconImage: JSX.Element): JSX.Element {
    return <div className={styles.personaImage}>{iconImage}</div>;
}

function getFirstLine(locationResult: LocationPersonaControlViewState): JSX.Element {
    return (
        <span className={classNames(styles.personaLineOne, styles.personaTextLine)}>
            {locationResult.displayName}
        </span>
    );
}

function shouldRenderCapacity(location: LocationPersonaControlViewState) {
    return (
        isFeatureEnabled('cal-mf-locationwellAdvancedRoomProperties') &&
        location.capacity !== undefined &&
        location.capacity !== null
    );
}

function shouldRenderCapabilities(location: LocationPersonaControlViewState) {
    return (
        isFeatureEnabled('cal-mf-locationwellAdvancedRoomProperties') &&
        ((location.audioDeviceName !== undefined && location.audioDeviceName !== null) ||
            (location.displayDeviceName !== undefined && location.displayDeviceName !== null) ||
            (location.videoDeviceName !== undefined && location.videoDeviceName !== null) ||
            location.isWheelChairAccessible)
    );
}

function renderCapacity(capacity: number) {
    return (
        <span>
            <Icon iconName={ControlIcons.Contact} className={styles.roomSecondaryTextIcon} />
            <span className={styles.roomSecondaryText}>{capacity}</span>
        </span>
    );
}

function renderCapabilities(
    audioDeviceName: string,
    displayDeviceName: string,
    videoDeviceName: string,
    isWheelChairAccessible: boolean
) {
    return (
        <span>
            {audioDeviceName && (
                <Icon
                    iconName={ControlIcons.Volume2}
                    className={styles.roomSecondaryCapabilityIcon}
                />
            )}
            {displayDeviceName && (
                <Icon
                    iconName={ControlIcons.TVMonitor}
                    className={styles.roomSecondaryCapabilityIcon}
                />
            )}
            {videoDeviceName && (
                <Icon
                    iconName={ControlIcons.Video}
                    className={styles.roomSecondaryCapabilityIcon}
                />
            )}
            {isWheelChairAccessible && (
                <Icon
                    iconName={ControlIcons.EaseOfAccess}
                    className={styles.roomSecondaryCapabilityIcon}
                />
            )}
        </span>
    );
}

function getSecondLine(
    locationResult: LocationPersonaControlViewState,
    isRoomWithAvailabilityInfo: boolean
): JSX.Element {
    let secondaryLine = (
        <span className={classNames(styles.personaLineTwo, styles.personaTextLine)}>
            {locationResult.displayAddress}
        </span>
    );
    if (isRoomWithAvailabilityInfo) {
        let { freebusyText, freebusyStyle } = getAvailabilityTextAndStyle(locationResult, styles);
        secondaryLine = (
            <div className={classNames(styles.personaLineTwo, styles.personaTextLine)}>
                <Icon
                    iconName={ControlIcons.Clock}
                    className={classNames(styles.icon, freebusyStyle)}
                />
                <span className={classNames(styles.roomSecondaryText, freebusyStyle)}>
                    {freebusyText}
                </span>
                {shouldRenderCapacity(locationResult) && renderCapacity(locationResult.capacity)}
                {shouldRenderCity(locationResult) && (
                    <>
                        <Icon
                            iconName={ControlIcons.POI}
                            className={styles.roomSecondaryTextIcon}
                        />
                        <span className={styles.roomSecondaryText}>
                            {locationResult.postalAddress.City}
                        </span>
                    </>
                )}
                {shouldRenderCapabilities(locationResult) &&
                    renderCapabilities(
                        locationResult.audioDeviceName,
                        locationResult.displayDeviceName,
                        locationResult.videoDeviceName,
                        locationResult.isWheelChairAccessible
                    )}
            </div>
        );
    }
    return secondaryLine;
}
